import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import {
    ApiErrorArgTypes,
    ContextValuesArgTypes,
    InitSentryArgTypes,
} from './types/SentryTypes';
import { isDevEnv } from './utils';

export const ErrorBoundary = Sentry.ErrorBoundary;

/**
 * base function for sentry logging in case of error
 * @param {string} errorMsg - Custom error string
 * @param {object} errorException - captured error/ exception
 * @param {ContextValuesArgTypes} contextValues - object with context information for Sentry.captureException method
 * @param {ApiErrorArgTypes} apiErrorInfo - api error info passed by consumer
 */
export const fireLogWithSentryBase = (
    errorMsg: string,
    errorException: any = {},
    contextValues: ContextValuesArgTypes = {},
    apiErrorKeys: ApiErrorArgTypes = {},
) => {
    let exception = new Error(errorMsg);
    const [fileName = '', methodName = '', errorInfo = ''] = errorMsg.split('-->');
    let { tags: additionalTags = {} } = contextValues;
    updateSentryContext(contextValues);

    // getting high events of local and session storage issue, disabling temporarily
    const isStorageIssue =
        errorInfo.toLowerCase().includes('storage') ||
        errorException?.message?.toLowerCase().includes('storage');

    let clickstreamSessionId = 'not_found';
    try {
        const sessionCookie = document.cookie
            .split('; ')
            .find((row) => row.startsWith('cs_sid='))
            ?.split('=')[1];
        if (sessionCookie) {
            clickstreamSessionId = JSON.parse(sessionCookie).uuid;
        }
    } catch (error) {
        clickstreamSessionId = 'not_found';
    }

    let tags: { [key: string]: any } = {
        isApiError: false,
        'errorSource.fileName': fileName,
        'errorSource.methodName': methodName,
        'errorSource.info': errorInfo,
        clickstreamSessionId,
        ...additionalTags,
    };

    if (errorException) {
        exception = errorException;
        // if errorException is non empty object, it may be api error object
        if (Object.keys(errorException).length) {
            const { status, config } = errorException;
            let { data = {} } = errorException;
            data = data ?? {};
            // api error
            if (status) {
                if (!tags.apiUrl) {
                    tags.apiUrl = config?.url;
                }
                const { responseCode = '', errorMsg = '' } = data;
                // since keys from api response differs for different projects, consumer can provide the apiErrorKeys for errorCode, errorMessage
                const errorCode = data[apiErrorKeys.errorCode] || responseCode,
                    errorMessage = data[apiErrorKeys.errorMessage] || errorMsg;
                tags.isApiError = true;
                tags['apiError.responseCode'] = status;
                tags['apiError.errorCode'] = errorCode;
                exception = new Error(
                    `${methodName} -> http ${status} error: ${errorCode}: ${errorMessage}`,
                );
            }
        } else if (typeof errorException === 'string') {
            exception = new Error(errorException);
        }
    }

    const extraPayload = {
        ...contextValues.extra,
    };

    try {
        extraPayload.jsonErrorException = JSON.stringify(errorException);

        const shouldSendEvent =
            !isStorageIssue || (isStorageIssue && Math.round(Math.random() * 100) === 1);

        if (shouldSendEvent && typeof Sentry !== 'undefined') {
            Sentry.withScope(() => {
                Sentry.captureException(exception, {
                    ...contextValues,
                    tags,
                    // errorException in extra
                    extra: extraPayload,
                });
            });
        }
    } catch (error) {
        //
    }

    return { contextValues, tags, extra: extraPayload };
};
/**
 * initialise sentry logging
 * @param {InitSentryArgTypes} initSentryObj - sentry init object
 */
export const initialiseSentry = (initSentryObj: InitSentryArgTypes): void => {
    const { dsn, release, environment, tracesSampleRate, isDebugMode, ...restOptions } =
        initSentryObj;
    updateSentryContext(restOptions);
    Sentry.init({
        dsn,
        release,
        environment,
        tracesSampleRate,
        beforeSend(event: any) {
            // Only send event if ENV is not DEV (=localhost)
            if (!isDevEnv() || isDebugMode) {
                return event;
            }
        },
        integrations: [new BrowserTracing()],
        transportOptions: {
            headers: {
                'X-Sentry-Auth': 'log4j2',
            },
        },
        ...restOptions,
    });
};

const updateSentryContext = (contextValues: ContextValuesArgTypes): void => {
    const tags = {
        'x-client': 'clixgateway',
        'x-client-auth': 'JGLI06Gt9d3Q1NaYCMtmk8sxibnVdEyXEKxGqrdJ2P8z4Y6ALC',
        ...(contextValues?.tags || {}),
    };
    for (const key in tags) {
        Sentry.setTag(key, tags[key]);
    }
};
